<template>
  <v-card class="tarjeta-reserva">
    <v-card-title class="titulo-modal">
      <span>
        <v-icon
          class="mr-1"
          size="20"
        >mdi-calendar-plus</v-icon>
        {{ esEdicion ? 'Editar reserva' : 'Nueva reserva' }}
      </span>
      <v-btn
        icon
        variant="text"
        size="small"
        @click="$emit('onClose')"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-alert
        v-if="error"
        type="error"
        density="compact"
        class="mb-3"
        closable
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <div class="etiqueta-seccion">
        Cliente
      </div>
      <v-text-field
        v-model="formulario.customerName"
        class="fg-blue mb-2"
        label="Nombre del cliente"
        variant="outlined"
        density="compact"
        autofocus
      />
      <v-text-field
        v-model="formulario.customerPhone"
        type="tel"
        class="fg-blue mb-2"
        label="Telefono (opcional)"
        variant="outlined"
        density="compact"
      />

      <div class="etiqueta-seccion">
        Cuando
      </div>
      <div class="grid-dos">
        <v-text-field
          v-model="fecha"
          type="date"
          class="fg-blue"
          label="Fecha"
          variant="outlined"
          density="compact"
        />
        <v-text-field
          v-model="hora"
          type="time"
          class="fg-blue"
          label="Hora"
          variant="outlined"
          density="compact"
        />
      </div>

      <div class="etiqueta-seccion">
        Detalles
      </div>
      <div class="grid-dos">
        <v-text-field
          v-model.number="formulario.people"
          type="number"
          min="1"
          class="fg-blue"
          label="Personas"
          variant="outlined"
          density="compact"
        />
        <v-select
          v-model="formulario.idTable"
          :items="opcionesMesa"
          item-title="titulo"
          item-value="valor"
          class="fg-blue"
          label="Mesa (opcional)"
          variant="outlined"
          density="compact"
        />
      </div>

      <v-textarea
        v-model="formulario.note"
        class="fg-blue mt-2"
        label="Nota (opcional)"
        placeholder="Cumpleanos, silla para bebe, alergias..."
        variant="outlined"
        density="compact"
        rows="2"
        :counter="500"
        maxlength="500"
      />
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn
        variant="text"
        @click="$emit('onClose')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        :loading="guardando"
        @click="guardar"
      >
        <v-icon start>
          mdi-content-save
        </v-icon>
        {{ esEdicion ? 'Guardar cambios' : 'Crear reserva' }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import apisReservas from '@/apis/apisReservas'

export default {
  name: 'DialogReserva',
  props: {
    idEmpresa: {
      type: [Number, String],
      required: true
    },
    // null cuando se esta creando una reserva nueva
    reserva: {
      type: Object,
      default: null
    },
    mesas: {
      type: Array,
      default: () => []
    },
    // 'YYYY-MM-DD': el dia que se esta viendo en el panel
    fechaPorDefecto: {
      type: String,
      required: true
    }
  },
  emits: ['onClose', 'alGuardar'],
  data() {
    return {
      formulario: {
        idReservation: null,
        idCompany: null,
        idTable: null,
        customerName: '',
        customerPhone: '',
        people: 2,
        note: '',
        state: null
      },
      fecha: this.fechaPorDefecto,
      hora: '19:00',
      guardando: false,
      error: ''
    }
  },
  computed: {
    esEdicion() {
      return this.formulario.idReservation !== null
    },
    opcionesMesa() {
      const opciones = [{ titulo: 'Sin asignar', valor: null }]

      this.mesas.forEach(mesa => {
        // Una mesa inactiva solo se ofrece si es la que ya tiene esta reserva,
        // para no perderla en silencio al editar.
        if (mesa.active || mesa.idTable === this.formulario.idTable) {
          opciones.push({
            titulo: mesa.zone
              ? `${mesa.name} (${mesa.capacity}) - ${mesa.zone}`
              : `${mesa.name} (${mesa.capacity})`,
            valor: mesa.idTable
          })
        }
      })

      return opciones
    }
  },
  created() {
    if (this.reserva) {
      this.cargarReserva(this.reserva)
    }
  },
  methods: {
    cargarReserva(reserva) {
      this.formulario = {
        idReservation: reserva.idReservation,
        idCompany: reserva.idCompany,
        idTable: reserva.idTable,
        customerName: reserva.customerName,
        customerPhone: reserva.customerPhone || '',
        people: reserva.people,
        note: reserva.note || '',
        state: reserva.state
      }

      // reservationDate llega como 'YYYY-MM-DDTHH:mm:ss'
      if (reserva.reservationDate) {
        const partes = reserva.reservationDate.split('T')
        this.fecha = partes[0]
        this.hora = partes[1] ? partes[1].substring(0, 5) : '19:00'
      }
    },
    guardar() {
      this.error = ''

      if (!this.formulario.customerName || this.formulario.customerName.trim() === '') {
        this.error = 'El nombre del cliente es obligatorio'
        return
      }
      if (!this.fecha || !this.hora) {
        this.error = 'La fecha y la hora son obligatorias'
        return
      }
      if (!this.formulario.people || this.formulario.people < 1) {
        this.error = 'El numero de personas debe ser al menos 1'
        return
      }

      this.guardando = true

      const reserva = Object.assign({}, this.formulario, {
        idCompany: Number(this.idEmpresa),
        // El backend espera el patron 'yyyy-MM-dd HH:mm:ss' con la T de por medio
        reservationDate: `${this.fecha}T${this.hora}:00`
      })

      apisReservas.guardarReserva(reserva)
        .then(() => {
          this.$emit('alGuardar')
        })
        .catch(error => {
          // Aqui cae el 409 cuando la mesa ya esta ocupada a esa hora
          this.error = error.message
        })
        .finally(() => {
          this.guardando = false
        })
    }
  }
}
</script>

<style scoped>
.tarjeta-reserva {
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
}
.titulo-modal {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1rem;
  font-weight: 700;
}
.etiqueta-seccion {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #1976d2;
  margin: 10px 0 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e3e3e3;
}
.etiqueta-seccion:first-of-type {
  margin-top: 0;
}
.grid-dos {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

@media screen and (min-width: 500px) {
  .grid-dos {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
